import type { Platform, SmartworkRuntimeHandle, SmartworkRuntimeResult } from "@/context/platform"
import {
  HOSTED_RUNTIME_REQUIRED_ROUTES,
  HOSTED_RUNTIME_UNAVAILABLE_MESSAGE,
  validateHostedRuntimeContract,
} from "@/services/openanalyst-hosted-runtime"
import { smartworkWorkspaceIdForPath } from "@/services/smartwork-project-identity"
import { normalizeOpenAnalystTenxBaseUrl, openAnalystWebBaseUrl } from "@/utils/openanalyst-hosts"
import { smartworkUrl } from "@/utils/smartwork"

type WebSmartworkRuntimeBootstrap = NonNullable<Platform["smartworkRuntimeBootstrap"]>
type WebSmartworkRuntimeBootstrapInput = Parameters<WebSmartworkRuntimeBootstrap>[0]

type SmartworkSession = {
  accessToken: string
  handles: SmartworkRuntimeHandle[]
  defaultHandle: string | null
}

type SmartworkControlPlaneProxyInput = {
  getServerUrl: () => string | null
  fetch?: typeof fetch
  requireServerUrl?: () => boolean
  refreshServerUrl?: () => string | null | Promise<string | null>
}

type WebSmartworkRuntimeBootstrapDeps = SmartworkControlPlaneProxyInput & {
  controlPlaneFetch: typeof fetch
  getControlPlaneToken: () => string | null
  getOpenAnalystToken?: () => string | null | Promise<string | null>
  refreshServerUrl?: () => string | null | Promise<string | null>
  startHeartbeat?: boolean
}

const utf8Encoder = typeof TextEncoder !== "undefined" ? new TextEncoder() : null
let activeHeartbeat: { key: string; timer: ReturnType<typeof setInterval> } | null = null
const missingHostedSmartworkBridge = new Set<string>()
const HOSTED_SMARTWORK_BRIDGE_MISSING_MESSAGE =
  "Hosted Smartwork bridge is unavailable for this runtime. The hosted backend must proxy /runtime/openanalyst/session/:id/smartwork/control-plane."
const HOSTED_SMARTWORK_RUNTIME_KIND = "cloud_worker"
const HOSTED_SMARTWORK_PROVIDER = "openanalyst_web"
const HOSTED_RUNTIME_SERVER_WAIT_TIMEOUT_MS = 10_000
const HOSTED_RUNTIME_SERVER_WAIT_INTERVAL_MS = 50
const HOSTED_RUNTIME_CONTRACT_WAIT_TIMEOUT_MS = 10_000
const HOSTED_RUNTIME_CONTRACT_WAIT_INTERVAL_MS = 50

function objectValue(value: unknown) {
  return value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : {}
}

function stringValue(value: unknown) {
  return typeof value === "string" && value.trim() ? value.trim() : null
}

function numberValue(value: unknown) {
  return typeof value === "number" && Number.isFinite(value) ? value : null
}

function normalizeHandle(value: unknown) {
  return stringValue(value)?.replace(/^@/, "") ?? null
}

function accessRoleValue(value: unknown): SmartworkRuntimeHandle["accessRole"] {
  const role = stringValue(value)?.toUpperCase()
  if (role === "CREATOR" || role === "OPERATOR" || role === "OWNER") return role
  return null
}

function normalizeHeaders(headers: Headers) {
  const blocked = new Set(["host", "connection", "content-length", "transfer-encoding"])
  return Object.fromEntries([...headers.entries()].filter(([key]) => !blocked.has(key.toLowerCase())))
}

function normalizeServerUrl(value: string | null) {
  if (!value?.trim()) return null
  try {
    const url = new URL(value)
    return url.toString().replace(/\/+$/, "")
  } catch {
    return null
  }
}

function hostedWebOriginFromServerUrl(value: string | null) {
  const serverUrl = normalizeServerUrl(value)
  if (!serverUrl) return null
  try {
    return new URL(serverUrl).origin
  } catch {
    return null
  }
}

function hostedWebOriginForExchange(value: string | null) {
  const origin = hostedWebOriginFromServerUrl(value)
  return openAnalystWebBaseUrl(origin ? { VITE_AUTH_URL: origin } : undefined)
}

function isServerRuntimeUrl(url: string, serverUrl: string | null) {
  if (!serverUrl) return false
  try {
    const target = new URL(url).toString()
    const runtimeUrl = new URL(serverUrl).toString().replace(/\/+$/, "")
    return target === runtimeUrl || target.startsWith(`${runtimeUrl}/`)
  } catch {
    return false
  }
}

function isHostedRuntimeSessionUrl(serverUrl: string | null) {
  if (!serverUrl) return false
  try {
    return /^\/runtime\/openanalyst\/session\/[^/]+\/?/.test(new URL(serverUrl).pathname)
  } catch {
    return false
  }
}

function isPublicHostedAppOrigin(serverUrl: string | null) {
  if (!serverUrl) return false
  try {
    const url = new URL(serverUrl)
    return (
      (
        url.hostname === "app.openanalyst.com" ||
        url.hostname === "app.basera.com" ||
        url.hostname === "cloud.openanalyst.com" ||
        url.hostname === "smartwork.arjun.tv" ||
        url.hostname === "smartwork.openanalyst.com"
      ) &&
      (url.pathname === "/" || url.pathname === "")
    )
  } catch {
    return false
  }
}

function wait(ms: number) {
  return new Promise<void>((resolve) => setTimeout(resolve, ms))
}

async function waitForHostedRuntimeServerUrl(input: SmartworkControlPlaneProxyInput) {
  const needsHostedRuntimeSession = () => !!input.requireServerUrl?.()
  const ready = (serverUrl: string | null) => !needsHostedRuntimeSession() || isHostedRuntimeSessionUrl(serverUrl)
  let serverUrl = normalizeServerUrl(input.getServerUrl())
  if (ready(serverUrl)) return serverUrl
  const deadline = Date.now() + HOSTED_RUNTIME_SERVER_WAIT_TIMEOUT_MS
  while (Date.now() < deadline) {
    await wait(HOSTED_RUNTIME_SERVER_WAIT_INTERVAL_MS)
    serverUrl = normalizeServerUrl(input.getServerUrl())
    if (ready(serverUrl)) return serverUrl
  }
  return serverUrl
}

function runtimeUnavailableResponse() {
  return new Response(
    JSON.stringify({
      error: {
        code: "runtime_unavailable",
        message: "Hosted OpenAnalyst runtime is still starting.",
      },
    }),
    {
      status: 503,
      headers: { "content-type": "application/json" },
    },
  )
}

function hostedSmartworkBridgeMissingResponse() {
  return new Response(
    JSON.stringify({
      error: {
        code: "hosted_runtime_route_missing",
        message: HOSTED_SMARTWORK_BRIDGE_MISSING_MESSAGE,
      },
    }),
    {
      status: 503,
      headers: { "content-type": "application/json" },
    },
  )
}

function hostedSmartworkBridgeMissingError(error: unknown) {
  return error instanceof Error && error.message.includes(HOSTED_SMARTWORK_BRIDGE_MISSING_MESSAGE)
}

async function hostedRuntimeSessionMismatch(response: Response) {
  if (response.status !== 401) return false
  const payload = objectValue(await response.clone().json().catch(() => null))
  const error = objectValue(payload.error)
  return (
    stringValue(error.code) === "HTTP_UNAUTHORIZED" &&
    stringValue(error.message) === "Runtime proxy session mismatch"
  )
}

async function hostedRuntimeContractError(input: { serverUrl: string; fetch: typeof fetch }) {
  if (!isHostedRuntimeSessionUrl(input.serverUrl)) return null
  return validateHostedRuntimeContract({
    serverUrl: input.serverUrl,
    fetch: input.fetch,
    requiredRoutes: HOSTED_RUNTIME_REQUIRED_ROUTES,
  })
    .then(() => null)
    .catch((error) => (error instanceof Error ? error.message : "Hosted runtime route contract check failed."))
}

async function waitForHostedRuntimeContractError(input: { serverUrl: string; fetch: typeof fetch }) {
  const staleSession = (error: string | null) => error?.includes("returned 401") ?? false
  const retryable = (error: string | null) =>
    (error?.startsWith(HOSTED_RUNTIME_UNAVAILABLE_MESSAGE) ?? false) && !staleSession(error)
  let contractError = await hostedRuntimeContractError(input)
  if (!retryable(contractError)) return contractError
  const deadline = Date.now() + HOSTED_RUNTIME_CONTRACT_WAIT_TIMEOUT_MS
  while (Date.now() < deadline) {
    await wait(HOSTED_RUNTIME_CONTRACT_WAIT_INTERVAL_MS)
    contractError = await hostedRuntimeContractError(input)
    if (!retryable(contractError)) return contractError
  }
  return contractError
}

async function waitForHostedRuntimeContract(input: {
  serverUrl: string
  fetch: typeof fetch
  refreshServerUrl?: () => string | null | Promise<string | null>
}) {
  const error = await waitForHostedRuntimeContractError({ serverUrl: input.serverUrl, fetch: input.fetch })
  if (!error || !error.includes("returned 401") || !input.refreshServerUrl || !isHostedRuntimeSessionUrl(input.serverUrl)) {
    return { serverUrl: input.serverUrl, error }
  }
  const refreshed = normalizeServerUrl(await Promise.resolve(input.refreshServerUrl()).catch(() => null))
  if (!refreshed || refreshed === input.serverUrl) return { serverUrl: input.serverUrl, error }
  return {
    serverUrl: refreshed,
    error: await waitForHostedRuntimeContractError({ serverUrl: refreshed, fetch: input.fetch }),
  }
}

async function hostedSmartworkBridgeMissing(response: Response) {
  if (response.status !== 404) return false
  const contentType = response.headers.get("content-type") ?? ""
  if (!contentType.toLowerCase().includes("application/json")) return true
  const payload = objectValue(await response.clone().json().catch(() => null))
  const error = objectValue(payload.error)
  const code = stringValue(error.code) ?? stringValue(payload.code)
  const message = stringValue(error.message) ?? stringValue(payload.message) ?? stringValue(payload.error)
  const normalizedCode = code?.toLowerCase()
  if (normalizedCode === "route_not_found" || normalizedCode === "method_not_allowed") return true
  if (normalizedCode === "not_found") return !message || /cannot\s+(post|get|put|patch|delete)|route|endpoint/i.test(message)
  if (normalizedCode) return false
  return !!message && /not found|cannot\s+(post|get|put|patch|delete)|route/i.test(message)
}

async function proxyPayload(input: RequestInfo | URL, init?: RequestInit) {
  const request = new Request(input, init)
  const method = request.method.toUpperCase()
  return {
    url: request.url,
    method,
    headers: normalizeHeaders(request.headers),
    ...(method === "GET" || method === "HEAD" ? {} : { body: await request.clone().text() }),
  }
}

function smartworkApiBaseUrl(input?: string | null) {
  return normalizeOpenAnalystTenxBaseUrl(input ?? "") || smartworkUrl()
}

async function sha256Hex(value: string) {
  if (typeof crypto !== "undefined" && crypto.subtle && utf8Encoder) {
    const buffer = await crypto.subtle.digest("SHA-256", utf8Encoder.encode(value))
    return Array.from(new Uint8Array(buffer))
      .map((byte) => byte.toString(16).padStart(2, "0"))
      .join("")
  }
  const nodeCrypto = await import("node:crypto")
  return nodeCrypto.createHash("sha256").update(value).digest("hex")
}

async function webRuntimeId(input: { serverUrl: string; workspaceId: string; workspacePath: string }) {
  return `rt_${(await sha256Hex(`openanalyst-web:${input.serverUrl}:${input.workspaceId}:${input.workspacePath}`)).slice(0, 24)}`
}

function runtimeResult(
  input: WebSmartworkRuntimeBootstrapInput,
  state: SmartworkRuntimeResult["state"],
  message: string | null,
): SmartworkRuntimeResult {
  const apiBaseUrl = smartworkApiBaseUrl(input.apiBaseUrl ?? input.smartworkUrl)
  return {
    state,
    connected: false,
    smartworkUrl: apiBaseUrl,
    apiBaseUrl,
    smartworkToken: null,
    handle: null,
    runtimeId: null,
    workspaceId: null,
    projectName: input.projectName ?? null,
    workspacePath: input.workspacePath,
    matchesWorkspace: null,
    localRuntimeUrl: null,
    handles: [],
    message,
  }
}

function runtimeTokenFromPayload(value: Record<string, unknown>) {
  const runtime = objectValue(value.runtime)
  return (
    stringValue(value.runtimeToken) ??
    stringValue(value.runtime_token) ??
    stringValue(value.token) ??
    stringValue(runtime.runtimeToken) ??
    stringValue(runtime.runtime_token) ??
    stringValue(runtime.token)
  )
}

function runtimeIdFromPayload(value: Record<string, unknown>, fallback: string) {
  const runtime = objectValue(value.runtime)
  return stringValue(value.runtimeId) ?? stringValue(value.runtime_id) ?? stringValue(runtime.runtimeId) ?? fallback
}

function accessTokenFromPayload(value: Record<string, unknown>, fallback: string | null) {
  return (
    stringValue(value.smartworkToken) ??
    stringValue(value.smartwork_token) ??
    stringValue(value.accessToken) ??
    stringValue(value.access_token) ??
    stringValue(value.token) ??
    fallback
  )
}

function bridgeDesktopIdFromPayload(value: Record<string, unknown> | null) {
  return stringValue(value?.desktopId) ?? stringValue(value?.desktop_id)
}

function handlesFromPayloads(input: {
  me?: Record<string, unknown> | null
  handlesPayload?: Record<string, unknown> | null
  sessionHandles?: SmartworkRuntimeHandle[]
}) {
  const seen = new Set<string>()
  const add = (handle: string | null, handleDisplay?: string | null, accessRole?: SmartworkRuntimeHandle["accessRole"]) => {
    if (!handle || seen.has(handle)) return []
    seen.add(handle)
    return [{ handle, handleDisplay, accessRole }]
  }
  const directHandle = objectValue(input.handlesPayload?.handle)
  return [
    ...(input.sessionHandles ?? []).flatMap((item) => add(item.handle, item.handleDisplay, item.accessRole)),
    ...add(
      normalizeHandle(directHandle.handle) ?? normalizeHandle(directHandle.name) ?? normalizeHandle(input.handlesPayload?.handle),
      stringValue(directHandle.handleDisplay) ?? stringValue(directHandle.handle_display) ?? stringValue(directHandle.displayName),
      accessRoleValue(directHandle.accessRole ?? directHandle.access_role ?? directHandle.role),
    ),
    ...(
      (Array.isArray(input.handlesPayload?.handles) ? input.handlesPayload.handles : null) ??
      (Array.isArray(input.handlesPayload?.items) ? input.handlesPayload.items : [])
    ).flatMap((item) => {
      const value = objectValue(item)
      return add(
        normalizeHandle(value.handle) ?? normalizeHandle(value.name),
        stringValue(value.handleDisplay) ?? stringValue(value.handle_display) ?? stringValue(value.displayName),
        accessRoleValue(value.accessRole ?? value.access_role ?? value.role),
      )
    }),
  ]
}

function defaultHandleFromPayload(value: Record<string, unknown> | null) {
  const tenant = objectValue(value?.tenant)
  const account = objectValue(value?.account)
  const handle = objectValue(value?.handle)
  return (
    normalizeHandle(value?.defaultHandle) ??
    normalizeHandle(value?.default_handle) ??
    normalizeHandle(handle.handle) ??
    normalizeHandle(handle.name) ??
    normalizeHandle(value?.handle) ??
    normalizeHandle(tenant.handle) ??
    normalizeHandle(account.defaultHandle)
  )
}

function selectRuntimeHandle(input: {
  requested?: string | null
  defaultHandle?: string | null
  handles: SmartworkRuntimeHandle[]
  forceHandleSelection?: boolean
}) {
  const requested = normalizeHandle(input.requested)
  const available = new Set(input.handles.map((item) => item.handle))
  const accessible = (handle: string | null) => !!handle && (available.size === 0 || available.has(handle))
  if (requested && accessible(requested)) return requested
  if (input.forceHandleSelection && input.handles.length > 0) return null
  const defaultHandle = normalizeHandle(input.defaultHandle)
  if (accessible(defaultHandle)) return defaultHandle
  if (input.handles.length === 1) return input.handles[0]?.handle ?? null
  return null
}

async function smartworkJson(input: { fetch: typeof fetch; apiBaseUrl: string; token: string; path: string; init?: RequestInit }) {
  const response = await input.fetch(new URL(input.path, input.apiBaseUrl), {
    ...input.init,
    headers: {
      accept: "application/json",
      authorization: `Bearer ${input.token}`,
      ...input.init?.headers,
    },
  })
  const payload = objectValue(await response.json().catch(() => null))
  if (response.ok) return payload
  const error = objectValue(payload.error)
  const message =
    stringValue(error.message) ??
    stringValue(payload.message) ??
    stringValue(payload.error_description) ??
    stringValue(payload.error) ??
    response.statusText
  throw new Error(`${input.path} returned ${response.status}${message ? `: ${message}` : ""}`)
}

async function exchangeOpenAnalystToken(input: {
  fetch: typeof fetch
  apiBaseUrl: string
  openAnalystToken: string
  hostedWebOrigin?: string | null
  runtimeId: string
  workspaceId: string
  workspacePath: string
  handle?: string | null
}) {
  return smartworkJson({
    fetch: input.fetch,
    apiBaseUrl: input.apiBaseUrl,
    token: input.openAnalystToken,
    path: "/v2/account/openanalyst/smartwork-token/exchange",
    init: {
      method: "POST",
      headers: {
        "content-type": "application/json",
        ...(input.hostedWebOrigin ? { "x-openanalyst-web-origin": input.hostedWebOrigin } : {}),
      },
      body: JSON.stringify({
        runtimeKind: HOSTED_SMARTWORK_RUNTIME_KIND,
        bindingKind: HOSTED_SMARTWORK_RUNTIME_KIND,
        bindingId: input.runtimeId,
        runtimeId: input.runtimeId,
        workspaceId: input.workspaceId,
        workspacePath: input.workspacePath,
        provider: HOSTED_SMARTWORK_PROVIDER,
        ...(normalizeHandle(input.handle) ? { handle: normalizeHandle(input.handle) } : {}),
      }),
    },
  })
}

async function discoverSession(input: {
  fetch: typeof fetch
  apiBaseUrl: string
  token: string
  sessionHandles?: SmartworkRuntimeHandle[]
  defaultHandle?: string | null
}) {
  const me = await smartworkJson({
    fetch: input.fetch,
    apiBaseUrl: input.apiBaseUrl,
    token: input.token,
    path: "/v2/account/openanalyst/me",
  })
  const handlesPayload = await smartworkJson({
    fetch: input.fetch,
    apiBaseUrl: input.apiBaseUrl,
    token: input.token,
    path: "/v2/handles",
  })
  return {
    accessToken: input.token,
    handles: handlesFromPayloads({ me, handlesPayload, sessionHandles: input.sessionHandles }),
    defaultHandle: defaultHandleFromPayload(me) ?? normalizeHandle(input.defaultHandle),
  }
}

async function registerRuntime(input: {
  fetch: typeof fetch
  apiBaseUrl: string
  token: string
  handle: string
  runtimeId: string
  workspaceId: string
  workspacePath: string
  projectName: string
}) {
  return smartworkJson({
    fetch: input.fetch,
    apiBaseUrl: input.apiBaseUrl,
    token: input.token,
    path: `/v2/handles/${encodeURIComponent(input.handle)}/smartwork/runtimes`,
    init: {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        kind: HOSTED_SMARTWORK_RUNTIME_KIND,
        runtimeId: input.runtimeId,
        workspaceId: input.workspaceId,
        workspacePath: input.workspacePath,
        projectName: input.projectName,
        provider: HOSTED_SMARTWORK_PROVIDER,
        workerProvider: HOSTED_SMARTWORK_PROVIDER,
        capabilities: {
          canMaterializeSkills: true,
          canRunOpenAnalyst: true,
        },
        providerMetadata: {
          client: "openanalyst-web",
          provider: HOSTED_SMARTWORK_PROVIDER,
          runtimeKind: HOSTED_SMARTWORK_RUNTIME_KIND,
          runtimeId: input.runtimeId,
          workspaceId: input.workspaceId,
          projectName: input.projectName,
          workspaceName: input.projectName,
        },
      }),
    },
  })
}

function startRuntimeHeartbeat(input: {
  fetch: typeof fetch
  apiBaseUrl: string
  token: string
  handle: string
  runtimeId: string
}) {
  const key = `${input.apiBaseUrl}\n${input.handle}\n${input.runtimeId}`
  if (activeHeartbeat?.key === key) return
  if (activeHeartbeat) clearInterval(activeHeartbeat.timer)
  const heartbeat = () =>
    smartworkJson({
      fetch: input.fetch,
      apiBaseUrl: input.apiBaseUrl,
      token: input.token,
      path: `/v2/handles/${encodeURIComponent(input.handle)}/smartwork/runtimes/${encodeURIComponent(input.runtimeId)}/heartbeat`,
      init: {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ status: "ready", connected: true }),
      },
    }).catch(() => undefined)
  void heartbeat()
  activeHeartbeat = { key, timer: setInterval(heartbeat, 30_000) }
}

export function createSmartworkControlPlaneProxyFetch(input: SmartworkControlPlaneProxyInput): typeof fetch {
  const fetchImpl = input.fetch ?? fetch
  return (async (requestInput, init) => {
    const serverUrl = normalizeServerUrl(input.getServerUrl())
    const target = requestInput instanceof Request ? requestInput.url : String(requestInput)
    const serverRuntimeUrl = isHostedRuntimeSessionUrl(serverUrl)
    const publicHostedAppOrigin = isPublicHostedAppOrigin(serverUrl)
    if (isServerRuntimeUrl(target, serverUrl) && (!publicHostedAppOrigin || isHostedRuntimeSessionUrl(target))) {
      return fetchImpl(requestInput, init)
    }
    if (!serverUrl || (input.requireServerUrl?.() && !serverRuntimeUrl) || (publicHostedAppOrigin && !serverRuntimeUrl)) {
      return input.requireServerUrl?.() || publicHostedAppOrigin ? runtimeUnavailableResponse() : fetchImpl(requestInput, init)
    }
    if (serverRuntimeUrl && missingHostedSmartworkBridge.has(serverUrl)) return hostedSmartworkBridgeMissingResponse()
    const payload = await proxyPayload(requestInput, init)
    const proxy = (runtimeUrl: string) =>
      fetchImpl(`${runtimeUrl}/smartwork/control-plane`, {
        method: "POST",
        headers: { "content-type": "application/json", accept: "application/json" },
        body: JSON.stringify(payload),
      })
    const response = await proxy(serverUrl)
    if (serverRuntimeUrl && (await hostedRuntimeSessionMismatch(response)) && input.refreshServerUrl) {
      const refreshed = normalizeServerUrl(await Promise.resolve(input.refreshServerUrl()).catch(() => null))
      if (refreshed && refreshed !== serverUrl) return proxy(refreshed)
    }
    if (serverRuntimeUrl && (await hostedSmartworkBridgeMissing(response))) {
      missingHostedSmartworkBridge.add(serverUrl)
      return hostedSmartworkBridgeMissingResponse()
    }
    return response
  }) as typeof fetch
}

export function createWebSmartworkRuntimeBootstrap(input: WebSmartworkRuntimeBootstrapDeps): WebSmartworkRuntimeBootstrap {
  return async (bootstrapInput) => {
    const serverUrl = await waitForHostedRuntimeServerUrl(input)
    if (!serverUrl) return runtimeResult(bootstrapInput, "provisioning_pending", "Hosted OpenAnalyst runtime is still starting.")
    if (input.requireServerUrl?.() && !isHostedRuntimeSessionUrl(serverUrl))
      return runtimeResult(bootstrapInput, "provisioning_pending", "Hosted OpenAnalyst runtime is still starting.")
    const contract = await waitForHostedRuntimeContract({
      serverUrl,
      fetch: input.fetch ?? fetch,
      refreshServerUrl: input.refreshServerUrl,
    })
    if (contract.error) return runtimeResult(bootstrapInput, "error", contract.error)
    const runtimeServerUrl = contract.serverUrl

    const apiBaseUrl = smartworkApiBaseUrl(bootstrapInput.apiBaseUrl ?? bootstrapInput.smartworkUrl)
    const controlPlaneToken = input.getControlPlaneToken()?.trim() || null
    const brokerOpenAnalystToken = (await Promise.resolve(input.getOpenAnalystToken?.()).catch(() => null))?.trim() || null
    const explicitOpenAnalystToken = bootstrapInput.rotateRuntimeToken ? bootstrapInput.openAnalystToken?.trim() || null : null
    // Smartwork exchange verifies the 10x route token; billing keeps using the nested OpenAnalyst token separately.
    const exchangeToken = explicitOpenAnalystToken || controlPlaneToken || brokerOpenAnalystToken
    if (input.requireServerUrl?.() && !exchangeToken) {
      return runtimeResult(bootstrapInput, "needs_auth", "OpenAnalyst sign-in is required.")
    }
    if (!controlPlaneToken && !exchangeToken) {
      return runtimeResult(bootstrapInput, "needs_auth", "OpenAnalyst sign-in is required.")
    }

    const workspaceId = await smartworkWorkspaceIdForPath(bootstrapInput.workspacePath)
    const runtimeId = await webRuntimeId({ serverUrl: runtimeServerUrl, workspaceId, workspacePath: bootstrapInput.workspacePath })
    const exchangeResult = exchangeToken
      ? await exchangeOpenAnalystToken({
          fetch: input.controlPlaneFetch,
          apiBaseUrl,
          openAnalystToken: exchangeToken,
          hostedWebOrigin: hostedWebOriginForExchange(runtimeServerUrl),
          runtimeId,
          workspaceId,
          workspacePath: bootstrapInput.workspacePath,
          handle: bootstrapInput.handle,
        }).then(
          (payload) => ({ payload, error: null }),
          (error) => ({ payload: null, error }),
        )
      : { payload: null, error: null }
    const exchangePayload = exchangeResult.payload
    const exchangedSmartworkToken = accessTokenFromPayload(exchangePayload ?? {}, null)
    const bridgeDesktopId = exchangePayload && exchangedSmartworkToken ? bridgeDesktopIdFromPayload(exchangePayload) : null
    if (input.requireServerUrl?.() && exchangeToken && !exchangedSmartworkToken) {
      const message =
        exchangeResult.error instanceof Error && exchangeResult.error.message.trim()
          ? exchangeResult.error.message
          : "Smartwork token exchange failed. Hosted Smartwork cannot start until the OpenAnalyst session can mint a Smartwork token."
      return runtimeResult(bootstrapInput, "needs_auth", message)
    }
    const smartworkToken = exchangedSmartworkToken ?? controlPlaneToken
    if (!smartworkToken) return runtimeResult(bootstrapInput, "needs_auth", "Smartwork token exchange is required.")

    try {
      const session: SmartworkSession =
        exchangePayload && exchangedSmartworkToken
          ? {
              accessToken: exchangedSmartworkToken,
              handles: handlesFromPayloads({ handlesPayload: exchangePayload }),
              defaultHandle: defaultHandleFromPayload(exchangePayload),
            }
          : await discoverSession({
              fetch: input.controlPlaneFetch,
              apiBaseUrl,
              token: smartworkToken,
              sessionHandles: handlesFromPayloads({ handlesPayload: exchangePayload }),
              defaultHandle: defaultHandleFromPayload(exchangePayload),
            })
      const handle = selectRuntimeHandle({
        requested: bootstrapInput.handle,
        defaultHandle: session.defaultHandle,
        handles: session.handles,
        forceHandleSelection: bootstrapInput.forceHandleSelection,
      })
      if (!handle && session.handles.length === 0) {
        return {
          ...runtimeResult(bootstrapInput, "provisioning_pending", "Smartwork handle provisioning is pending."),
          connected: true,
          apiBaseUrl,
          smartworkUrl: apiBaseUrl,
          runtimeId,
          workspaceId,
          handles: [],
        }
      }
      if (!handle) {
        return {
          ...runtimeResult(bootstrapInput, "needs_handle", "Choose a Smartwork handle."),
          connected: true,
          apiBaseUrl,
          smartworkUrl: apiBaseUrl,
          runtimeId,
          workspaceId,
          handles: session.handles,
        }
      }

      const projectName = bootstrapInput.projectName?.trim() || bootstrapInput.workspacePath.split(/[\\/]/).filter(Boolean).at(-1) || "OpenAnalyst project"
      const registered = await registerRuntime({
        fetch: input.controlPlaneFetch,
        apiBaseUrl,
        token: session.accessToken,
        handle,
        runtimeId,
        workspaceId,
        workspacePath: bootstrapInput.workspacePath,
        projectName,
      })
      const registeredRuntimeId = runtimeIdFromPayload(registered, runtimeId)
      if (input.startHeartbeat !== false) {
        startRuntimeHeartbeat({
          fetch: input.controlPlaneFetch,
          apiBaseUrl,
          token: runtimeTokenFromPayload(registered) ?? session.accessToken,
          handle,
          runtimeId: registeredRuntimeId,
        })
      }

      return {
        state: "ready",
        connected: true,
        smartworkUrl: apiBaseUrl,
        apiBaseUrl,
        smartworkToken: session.accessToken,
        handle,
        runtimeId: registeredRuntimeId,
        workspaceId,
        projectName,
        workspacePath: bootstrapInput.workspacePath,
        matchesWorkspace: true,
        localRuntimeUrl: runtimeServerUrl,
        bridgeDesktopId,
        bridgeDesktopIdSource: bridgeDesktopId ? "hosted_exchange" : "none",
        handles: session.handles,
        message: null,
      }
    } catch (error) {
      if (hostedSmartworkBridgeMissingError(error)) return runtimeResult(bootstrapInput, "error", HOSTED_SMARTWORK_BRIDGE_MISSING_MESSAGE)
      throw error
    }
  }
}
